import {
  AssetByMeter,
  CancelTaskAssignPayload,
  CreateMeterStrategyPayload,
  CreatePlanningPayload,
  CreatePlanningResponse,
  CreatePlanningTaskAssignPayload,
  GroupedByAssetParams,
  GroupedByAssetResponse,
  ImportAuditDetail,
  ImportAuditSummary,
  ListPlanningsParams,
  ListPlanningsResponse,
  ListTaskAssignsParams,
  ListTaskAssignsResponse,
  MeterStrategy,
  PlanningChangeLog,
  PlanningDetail,
  PlanningTaskAssign,
  PlanningType,
  PlanningTypePayload,
  ProjectionPreview,
  ProjectionPreviewParams,
  ReassignTaskAssignPayload,
  StartImportResponse,
  UpdatePlanningPayload,
} from '../types/plannings';
import { client } from './index';

export function appendList(params: URLSearchParams, key: string, values: number[] | undefined) {
  if (!values?.length) return;
  values.forEach((value) => params.append(`${key}[]`, value.toString()));
}

function buildListQueryString(input: ListPlanningsParams): string {
  const params = new URLSearchParams();
  if (input.search) params.append('search', input.search);
  if (input.state && input.state !== 'all') params.append('state', input.state);
  if (input.date_from) params.append('date_from', input.date_from);
  if (input.date_to) params.append('date_to', input.date_to);
  appendList(params, 'supervisor_ids', input.supervisor_ids);
  appendList(params, 'asset_ids', input.asset_ids);
  appendList(params, 'asset_type_ids', input.asset_type_ids);
  appendList(params, 'planning_type_ids', input.planning_type_ids);
  appendList(params, 'location_ids', input.location_ids);
  if (input.page) params.append('page', input.page.toString());
  if (input.per_page) params.append('per_page', input.per_page.toString());
  if (input.sort) params.append('sort', input.sort);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ---------- Plannings ----------

/**
 * GET /plannings — the backend wraps the response in `{ plannings, meta }`
 * with `meta.total`, `meta.has_more` y `meta.counts` por estado. La FE no
 * deriva nada: las cifras del dashboard y la paginación vienen del server.
 */
async function listPlannings(input: ListPlanningsParams): Promise<ListPlanningsResponse> {
  const response = await client.get<ListPlanningsResponse>(
    `/plannings${buildListQueryString(input)}`,
  );
  return response.data;
}

async function getPlanning(
  id: number,
  options: { includeTaskAssigns?: boolean } = {},
): Promise<PlanningDetail> {
  // `includeTaskAssigns=false` evita serializar todas las TaskAssigns en el
  // detalle (la vista de Planning ahora las consume paginadas por ciclo).
  // El wizard de edición lo deja en el default `true` porque sigue usando
  // `planning.task_assigns` para `sync_submitted_tasks`.
  const params = options.includeTaskAssigns === false ? '?include_task_assigns=false' : '';
  const response = await client.get<PlanningDetail>(`/plannings/${id}${params}`);
  return response.data;
}

function buildTaskAssignsQueryString(input: ListTaskAssignsParams): string {
  const params = new URLSearchParams();
  params.append('cycle', input.cycle.toString());
  if (input.page) params.append('page', input.page.toString());
  if (input.per_page) params.append('per_page', input.per_page.toString());
  if (input.acceptance_status && input.acceptance_status !== 'all') {
    params.append('acceptance_status', input.acceptance_status);
  }
  if (input.search?.trim()) params.append('search', input.search.trim());
  appendList(params, 'mobile_user_ids', input.mobile_user_ids);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

async function listTaskAssigns(input: ListTaskAssignsParams): Promise<ListTaskAssignsResponse> {
  const response = await client.get<ListTaskAssignsResponse>(
    `/plannings/${input.planning_id}/task_assigns${buildTaskAssignsQueryString(input)}`,
  );
  return response.data;
}

async function createPlanning(payload: CreatePlanningPayload): Promise<CreatePlanningResponse> {
  const response = await client.post<CreatePlanningResponse>('/plannings', { planning: payload });
  return response.data;
}

async function updatePlanning(id: number, payload: UpdatePlanningPayload): Promise<PlanningDetail> {
  const response = await client.patch<PlanningDetail>(`/plannings/${id}`, { planning: payload });
  return response.data;
}

async function deletePlanning(id: number): Promise<void> {
  await client.delete(`/plannings/${id}`);
}

async function publishPlanning(id: number): Promise<PlanningDetail> {
  const response = await client.post<PlanningDetail>(`/plannings/${id}/publish`);
  return response.data;
}

async function pausePlanning(id: number): Promise<PlanningDetail> {
  const response = await client.post<PlanningDetail>(`/plannings/${id}/pause`);
  return response.data;
}

async function resumePlanning(id: number): Promise<PlanningDetail> {
  const response = await client.post<PlanningDetail>(`/plannings/${id}/resume`);
  return response.data;
}

async function archivePlanning(id: number): Promise<PlanningDetail> {
  const response = await client.post<PlanningDetail>(`/plannings/${id}/archive`);
  return response.data;
}

async function unarchivePlanning(id: number): Promise<PlanningDetail> {
  const response = await client.post<PlanningDetail>(`/plannings/${id}/unarchive`);
  return response.data;
}

// ---------- PlanningTypes ----------

async function listPlanningTypes(): Promise<PlanningType[]> {
  // Rails returns a bare array; tolerate the wrapped shape too for forward-compat.
  const response = await client.get<PlanningType[] | { planning_types: PlanningType[] }>(
    '/planning_types',
  );
  return Array.isArray(response.data) ? response.data : response.data.planning_types;
}

async function createPlanningType(payload: PlanningTypePayload): Promise<PlanningType> {
  const response = await client.post<PlanningType>('/planning_types', { planning_type: payload });
  return response.data;
}

async function updatePlanningType(id: number, payload: PlanningTypePayload): Promise<PlanningType> {
  const response = await client.patch<PlanningType>(`/planning_types/${id}`, {
    planning_type: payload,
  });
  return response.data;
}

async function deletePlanningType(id: number): Promise<void> {
  await client.delete(`/planning_types/${id}`);
}

// ---------- Meter planning (W1) ----------

async function listMeterStrategies(): Promise<MeterStrategy[]> {
  const response = await client.get<MeterStrategy[]>('/meter_strategies');
  return response.data;
}

async function createMeterStrategy(payload: CreateMeterStrategyPayload): Promise<MeterStrategy> {
  const response = await client.post<MeterStrategy>('/meter_strategies', payload);
  return response.data;
}

async function deleteMeterStrategy(id: number): Promise<void> {
  await client.delete(`/meter_strategies/${id}`);
}

/**
 * Append a positive, finite number. Non-finite (NaN) or non-positive values are
 * omitted so the backend falls back to its default instead of receiving a
 * degenerate value (e.g. `manual_daily_average=0` would otherwise be read as a
 * manual rate of 0, contradicting the `automatic` mode the UI derives for it).
 */
export function appendPositive(search: URLSearchParams, key: string, value: number | undefined): void {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    search.append(key, value.toString());
  }
}

/** Like appendPositive but keeps 0, for values where zero is meaningful. */
export function appendDefined(
  search: URLSearchParams,
  key: string,
  value: number | null | undefined,
): void {
  if (typeof value === 'number' && Number.isFinite(value)) {
    search.append(key, value.toString());
  }
}

/**
 * GET /plannings/projection_preview. A safe, idempotent read (persists nothing),
 * so the projection params travel in the query string.
 */
export function buildProjectionPreviewQueryString(params: ProjectionPreviewParams): string {
  const search = new URLSearchParams();
  search.append('meter_strategy_id', params.meter_strategy_id.toString());
  search.append('asset_id', params.asset_id.toString());
  search.append('meter_id', params.meter_id.toString());
  if (params.cumulative !== undefined) search.append('cumulative', params.cumulative.toString());
  appendPositive(search, 'manual_daily_average', params.manual_daily_average);
  appendPositive(search, 'calculation_window_months', params.calculation_window_months);
  appendPositive(search, 'horizon', params.horizon);
  // Not appendPositive: a reading of 0 is legal for a strategy's first milestone, and
  // dropping it would send a half declaration.
  appendDefined(search, 'entry_level_id', params.entry_level_id);
  appendDefined(search, 'entry_milestone_value', params.entry_milestone_value);
  appendDefined(search, 'entry_meter_value', params.entry_meter_value);
  return search.toString();
}

async function projectionPreview(
  payload: ProjectionPreviewParams,
): Promise<ProjectionPreview> {
  const response = await client.get<ProjectionPreview>(
    `/plannings/projection_preview?${buildProjectionPreviewQueryString(payload)}`,
  );
  return response.data;
}

async function assetsByMeter(meterId: number): Promise<AssetByMeter[]> {
  const response = await client.get<AssetByMeter[]>(
    `/plannings/assets_by_meter?meter_id=${meterId}`,
  );
  return response.data;
}

/**
 * GET /plannings/grouped_by_asset — the Assets View. Same filter params as
 * `listPlannings`, so it reuses `buildListQueryString` rather than a second
 * query-string builder.
 */
async function groupedByAsset(input: GroupedByAssetParams): Promise<GroupedByAssetResponse> {
  const response = await client.get<GroupedByAssetResponse>(
    `/plannings/grouped_by_asset${buildListQueryString(input)}`,
  );
  return response.data;
}

/**
 * POST /plannings/track_view_opened — usage metric shared by both view
 * modes. Neither `GET /plannings` nor `grouped_by_asset` can double as this
 * signal: both are refetched on every filter/pagination/search change, so
 * logging there would conflate every refetch with a distinct "view opened".
 * Called once on a genuine mount of a view (see PlanningsList.tsx) — never
 * on an in-session toggle switch, which `view_mode_changed` already logs.
 */
async function trackViewOpened(view: 'general' | 'assets'): Promise<void> {
  await client.post('/plannings/track_view_opened', { view });
}

// ---------- Import ----------

async function downloadImportTemplate(): Promise<Blob> {
  const response = await client.get<Blob>('/plannings/import_template.xlsx', {
    responseType: 'blob',
  });
  return response.data;
}

async function startImport(file: File): Promise<StartImportResponse> {
  const formData = new FormData();
  formData.append('file', file);
  // No setear `Content-Type` manualmente. Cuando el body es un FormData,
  // axios genera el header `multipart/form-data; boundary=...` con el
  // boundary correcto. Pasarlo a mano omite el boundary y el server
  // responde 400 ("Missing boundary").
  const response = await client.post<StartImportResponse>('/plannings/import', formData);
  return response.data;
}

async function listImportAudits(): Promise<ImportAuditSummary[]> {
  const response = await client.get<{ audits: ImportAuditSummary[] }>('/plannings/import_audits');
  return response.data.audits;
}

async function getImportAudit(id: number): Promise<ImportAuditDetail> {
  const response = await client.get<ImportAuditDetail>(`/plannings/import_audits/${id}`);
  return response.data;
}

// ---------- TaskAssign actions specific to Plannings ----------

async function cancelTaskAssign(
  taskAssignId: number,
  payload: CancelTaskAssignPayload,
): Promise<void> {
  await client.post(`/task_assigns/${taskAssignId}/cancel`, payload);
}

async function reassignTaskAssign(
  taskAssignId: number,
  payload: ReassignTaskAssignPayload,
): Promise<void> {
  await client.post(`/task_assigns/${taskAssignId}/reassign`, payload);
}

// Type-preserving group reassignment (GroupTaskAssign feature): moves a group
// task's cycle row to a different group. For a planning this affects only that
// cycle. Mirrors reassignTaskAssign but targets the group endpoint.
async function reassignGroupTask(
  groupTaskAssignId: number,
  payload: { groupId: number },
): Promise<void> {
  await client.post(`/task_assigner/groups/${groupTaskAssignId}/reassign`, {
    group_id: payload.groupId,
  });
}

async function createPlanningTaskAssign(
  payload: CreatePlanningTaskAssignPayload,
): Promise<PlanningTaskAssign> {
  const { planning_id, ...taskAssignFields } = payload;
  const response = await client.post<PlanningTaskAssign>(`/plannings/${planning_id}/task_assigns`, {
    task_assign: taskAssignFields,
  });
  return response.data;
}

/**
 * Edita una tarea de ciclo 0 (template) con la misma semántica que la
 * creación: el backend re-traduce `day_in_cycle`/`time_of_day` a un
 * `start_time` absoluto anclado en `planning.start_date` y preserva
 * `form_id`/`cycle_day`. `planning_id` viaja en la URL; el resto en
 * `task_assign`. Mismo payload que `createPlanningTaskAssign`.
 */
async function updatePlanningTaskAssign(
  taskAssignId: number,
  payload: CreatePlanningTaskAssignPayload,
): Promise<PlanningTaskAssign> {
  const { planning_id, ...taskAssignFields } = payload;
  const response = await client.patch<PlanningTaskAssign>(
    `/plannings/${planning_id}/task_assigns/${taskAssignId}`,
    { task_assign: taskAssignFields },
  );
  return response.data;
}

// Change history of a planning (most recent first).
async function getPlanningChangeLogs(id: number): Promise<PlanningChangeLog[]> {
  const response = await client.get<{ change_logs: PlanningChangeLog[] }>(
    `/plannings/${id}/change_logs`,
  );
  return response.data.change_logs;
}

// Shared responsibles list mutations (shared mode). Both return the
// refreshed planning detail.
async function addPlanningResponsible(
  planningId: number,
  target: { mobile_user_id?: number; group_id?: number },
): Promise<PlanningDetail> {
  const response = await client.post<PlanningDetail>(`/plannings/${planningId}/responsibles`, {
    responsible: target,
  });
  return response.data;
}

async function removePlanningResponsible(
  planningId: number,
  responsibleId: number,
  options: { cancelOpenTasks?: boolean } = {},
): Promise<PlanningDetail> {
  const query = options.cancelOpenTasks ? '?cancel_open_tasks=true' : '';
  const response = await client.delete<PlanningDetail>(
    `/plannings/${planningId}/responsibles/${responsibleId}${query}`,
  );
  return response.data;
}

export const planningsApi = {
  listPlannings,
  getPlanning,
  getPlanningChangeLogs,
  addPlanningResponsible,
  removePlanningResponsible,
  listTaskAssigns,
  createPlanning,
  updatePlanning,
  deletePlanning,
  publishPlanning,
  pausePlanning,
  resumePlanning,
  archivePlanning,
  unarchivePlanning,
  listPlanningTypes,
  createPlanningType,
  updatePlanningType,
  deletePlanningType,
  listMeterStrategies,
  createMeterStrategy,
  deleteMeterStrategy,
  projectionPreview,
  assetsByMeter,
  groupedByAsset,
  trackViewOpened,
  downloadImportTemplate,
  startImport,
  listImportAudits,
  getImportAudit,
  cancelTaskAssign,
  reassignTaskAssign,
  reassignGroupTask,
  createPlanningTaskAssign,
  updatePlanningTaskAssign,
};
